/* eslint-disable react/prop-types */
// import and use only default module in order to allow spyOn(React, 'useState') in testing
import React from 'react';
import './component-video.less';
import YouTubeVideo from './subcomponents/youtube-video';
import VimeoVideo from './subcomponents/vimeo-video';
import LoomVideo from './subcomponents/loom-video';
import type { GqlComponentVideo } from '../../../content/types/content/component-video';
import { sentryCaptureException } from '../../utils/sentry/sentry-client';
import { getVideoInfo } from './utils/get-video-info';

type ComponentVideoProps = Pick<GqlComponentVideo, 'videoUrl'> &
    Partial<Pick<GqlComponentVideo, 'title' | 'description'>>;

const ComponentVideo: React.FC<ComponentVideoProps> = ({
    videoUrl,
    title,
    description
}) => {
    const [hasError, setHasError] = React.useState(false);

    const handleError = React.useCallback((errorMessage: string) => {
        const error = new Error(errorMessage);
        sentryCaptureException({
            exception: error
        });
        setHasError(true);
    }, []);

    const videoElement = React.useMemo(() => {
        // Extract video info from URL
        const videoInfo = getVideoInfo(videoUrl);

        if (!videoInfo) {
            handleError(
                `Unsupported video service or invalid URL: ${videoUrl}`
            );
            return null;
        }

        // Route to appropriate video component based on service
        switch (videoInfo.service) {
            case 'YouTube':
                return (
                    <YouTubeVideo
                        videoId={videoInfo.videoId}
                        onError={handleError}
                    />
                );
            case 'Vimeo':
                return (
                    <VimeoVideo
                        videoId={videoInfo.videoId}
                        onError={handleError}
                    />
                );
            case 'Loom':
                return (
                    <LoomVideo
                        videoId={videoInfo.videoId}
                        title={title}
                        description={description}
                        onError={handleError}
                    />
                );
            default: {
                // This will raise a TS error if a new type is added without including a case to handle it.
                const unknownService: never = videoInfo.service;
                handleError(`Unsupported video service: ${unknownService}`);
                return null;
            }
        }
    }, [videoUrl, title, description, handleError]);

    return (
        <div data-testid="component-video" className="component-video">
            {title && <h3 className="component-video__title">{title}</h3>}
            {hasError ? (
                <div className="component-video--error">
                    <p>
                        This video cannot be displayed. Please contact support
                        if you believe this is an error.
                    </p>
                </div>
            ) : (
                videoElement
            )}
            {description && (
                <p className="component-video__description">{description}</p>
            )}
        </div>
    );
};

export default ComponentVideo;
